import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../../../api/apiClient";
import "../../../assets/css/admin/organization/OrgBoardAdmin.css";
import "../../../assets/css/admin/AdminCommon.css";

import SearchD from "../../../assets/img/Search_D.svg";
import SearchF from "../../../assets/img/Search_F.svg";

const unitList = [
  { key: "scitech", name: "과학기술대학", collegeId: 1 },
  { key: "global", name: "글로벌융합대학", collegeId: 2 },
  { key: "future", name: "미래인재대학", collegeId: 3 },
  { key: "pharm", name: "약학대학", collegeId: 4 },
  { key: "artdesign", name: "아트앤디자인대학", collegeId: 5 },
  { key: "club", name: "동아리연합회", collegeId: 6 },
  { key: "audit", name: "감사위원회", collegeId: 7 },
];

const categoryOptions = [
  { label: "전체", value: "ALL" },
  { label: "회계", value: "BUDGET" },
  { label: "행사·사업", value: "EVENT" },
  { label: "행정·운영", value: "ADMINISTRATION" },
  { label: "기타", value: "ETC" },
];

const searchOptions = [
  { label: "제목", value: "TITLE" },
  { label: "내용", value: "CONTENT" },
];

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}.${mm}.${dd}`;
};

const getTimeValue = (value) => {
  if (!value) return 0;

  const date = new Date(value);
  const time = date.getTime();

  return Number.isNaN(time) ? 0 : time;
};

const getPagedArray = (result) => {
  const data = result?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.contents)) return data.contents;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;

  return [];
};

export default function OrgBoardAdmin() {
  const navigate = useNavigate();
  const { unit = "scitech" } = useParams();

  const validUnit = unitList.some((item) => item.key === unit)
    ? unit
    : "scitech";

  const selectedUnit =
    unitList.find((item) => item.key === validUnit) || unitList[0];

  const selectedUnitName = selectedUnit.name;
  const collegeId = selectedUnit.collegeId;

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("ALL");

  const [searchType, setSearchType] = useState("TITLE");
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const [boardList, setBoardList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const searchIcon = keyword.trim() ? SearchF : SearchD;

  const selectedCategoryLabel =
    categoryOptions.find((item) => item.value === selectedCategory)?.label ||
    "전체";

  const selectedSearchLabel =
    searchOptions.find((item) => item.value === searchType)?.label || "제목";

  const normalizeBoardItem = useCallback((item) => {
    const createdAtRaw =
      item.createdAt || item.createdDate || item.updatedAt || "";

    return {
      id: item.collegeFormsId ?? item.id,
      title: item.title ?? "",
      writer:
        item.createdByName ??
        item.name ??
        item.writer ??
        item.createdBy ??
        "관리자",
      date: formatDate(createdAtRaw),
      createdAtRaw,
      viewer: item.viewer ?? 0,
    };
  }, []);

  const fetchBoardList = useCallback(
    async ({
      categoryValue = "ALL",
      searchTypeValue = "TITLE",
      keywordValue = "",
    } = {}) => {
      try {
        setLoading(true);

        const params = new URLSearchParams({
          listCategory: categoryValue,
          keyWordCategory: searchTypeValue,
          page: "0",
          size: "100",
        });

        const trimmedKeyword = keywordValue.trim();

        if (trimmedKeyword) {
          params.set("keyWord", trimmedKeyword);
        }

        const result = await apiClient(
          `/api/colleges/${collegeId}/form?${params.toString()}`
        );

        console.log(`${selectedUnitName} 게시판 목록 응답:`, result);

        const list = getPagedArray(result).map(normalizeBoardItem);

        setBoardList(list);
      } catch (error) {
        console.error("조직별 게시판 목록 조회 오류:", error);
        alert(error.message || "게시글 목록 조회 중 오류가 발생했습니다.");
        setBoardList([]);
      } finally {
        setLoading(false);
      }
    },
    [collegeId, selectedUnitName, normalizeBoardItem]
  );

  useEffect(() => {
    setCategoryOpen(false);
    setSearchOpen(false);
    setSelectedCategory("ALL");
    setSearchType("TITLE");
    setKeyword("");
    setCurrentPage(1);

    fetchBoardList({
      categoryValue: "ALL",
      searchTypeValue: "TITLE",
      keywordValue: "",
    });
  }, [collegeId, fetchBoardList]);

  const sortedList = useMemo(() => {
    return [...boardList].sort((a, b) => {
      const timeA = getTimeValue(a.createdAtRaw);
      const timeB = getTimeValue(b.createdAtRaw);

      if (timeA !== timeB) {
        return timeB - timeA;
      }

      return Number(b.id || 0) - Number(a.id || 0);
    });
  }, [boardList]);

  const totalPages = Math.ceil(sortedList.length / itemsPerPage);

  const safeCurrentPage =
    totalPages === 0
      ? 1
      : Math.min(Math.max(currentPage, 1), totalPages);

  const pagedList = sortedList.slice(
    (safeCurrentPage - 1) * itemsPerPage,
    safeCurrentPage * itemsPerPage
  );

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSelectCategory = (value) => {
    setSelectedCategory(value);
    setCategoryOpen(false);
    setCurrentPage(1);

    fetchBoardList({
      categoryValue: value,
      searchTypeValue: searchType,
      keywordValue: keyword,
    });
  };

  const handleSelectSearchType = (value) => {
    setSearchType(value);
    setSearchOpen(false);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);

    fetchBoardList({
      categoryValue: selectedCategory,
      searchTypeValue: searchType,
      keywordValue: keyword,
    });
  };

  const handleAddMove = () => {
    navigate("write");
  };

  const handleEditMove = (id) => {
    navigate(`edit/${id}`);
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("삭제하시겠습니까?");
    if (!confirmDelete) return;

    try {
      await apiClient(`/api/colleges/${collegeId}/form/${id}`, {
        method: "DELETE",
      });

      setBoardList((prev) => prev.filter((item) => item.id !== id));
      alert("삭제되었습니다.");
    } catch (error) {
      console.error("조직별 게시글 삭제 오류:", error);
      alert(error.message || "삭제 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="org-board-page admin-scroll-x">
      <div className="org-board-inner">
        <h2 className="org-board-title">{selectedUnitName} 게시판 관리</h2>

        <div className="org-board-admin-toolbar">
          <button
            type="button"
            className="org-board-add-btn"
            onClick={handleAddMove}
            disabled={loading}
          >
            +
          </button>

          <div className="org-board-admin-filter">
            <div className="org-board-filter-left">
              <div className="org-board-filter-wrap">
                <button
                  type="button"
                  className={
                    categoryOpen
                      ? "org-board-filter-btn is-open"
                      : "org-board-filter-btn"
                  }
                  onClick={() => {
                    setCategoryOpen((prev) => !prev);
                    setSearchOpen(false);
                  }}
                >
                  <span>{selectedCategoryLabel}</span>

                  <span
                    className="org-board-filter-arrow"
                    aria-hidden="true"
                  />
                </button>

                {categoryOpen && (
                  <div className="org-board-filter-menu">
                    {categoryOptions.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => handleSelectCategory(item.value)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="org-board-filter-right">
              <div className="org-board-filter-wrap">
                <button
                  type="button"
                  className={
                    searchOpen
                      ? "org-board-filter-btn is-open"
                      : "org-board-filter-btn"
                  }
                  onClick={() => {
                    setSearchOpen((prev) => !prev);
                    setCategoryOpen(false);
                  }}
                >
                  <span>{selectedSearchLabel}</span>

                  <span
                    className="org-board-filter-arrow"
                    aria-hidden="true"
                  />
                </button>

                {searchOpen && (
                  <div className="org-board-filter-menu">
                    {searchOptions.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => handleSelectSearchType(item.value)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="org-board-search-box">
                <input
                  type="text"
                  placeholder="검색어를 입력하세요."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />

                <button
                  type="button"
                  className="org-board-search-btn"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  <img src={searchIcon} alt="검색" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="org-board-table">
          <div className="org-board-table-header">
            <div>No</div>
            <div>제목</div>
            <div>작성자</div>
            <div>작성일</div>
            <div>관리</div>
          </div>

          {loading ? (
            <div className="org-board-empty">불러오는 중입니다.</div>
          ) : pagedList.length > 0 ? (
            pagedList.map((item, index) => {
              const displayNo = String(
                sortedList.length -
                  ((safeCurrentPage - 1) * itemsPerPage + index)
              ).padStart(3, "0");

              return (
                <div className="org-board-table-row" key={item.id}>
                  <div>{displayNo}</div>

                  <div>{item.title}</div>
                  <div>{item.writer}</div>
                  <div>{item.date}</div>

                  <div className="org-board-actions">
                    <button
                      type="button"
                      onClick={() => handleEditMove(item.id)}
                    >
                      수정
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="org-board-empty">등록된 게시글이 없습니다.</div>
          )}
        </div>

        <div className="org-board-pagination">
          <button
            type="button"
            onClick={() => setCurrentPage(1)}
            disabled={safeCurrentPage === 1}
          >
            ‹‹
          </button>

          <button
            type="button"
            onClick={() =>
              setCurrentPage((prev) => Math.max(prev - 1, 1))
            }
            disabled={safeCurrentPage === 1}
          >
            ‹
          </button>

          {Array.from(
            { length: totalPages || 1 },
            (_, index) => index + 1
          ).map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              className={
                safeCurrentPage === pageNumber ? "active" : ""
              }
              onClick={() => setCurrentPage(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}

          <button
            type="button"
            onClick={() =>
              setCurrentPage((prev) =>
                Math.min(prev + 1, totalPages || 1)
              )
            }
            disabled={safeCurrentPage === (totalPages || 1)}
          >
            ›
          </button>

          <button
            type="button"
            onClick={() => setCurrentPage(totalPages || 1)}
            disabled={safeCurrentPage === (totalPages || 1)}
          >
            ››
          </button>
        </div>
      </div>
    </div>
  );
}